/**
 * newHomeHelper.js
 *
 * Utility functions for the NewHome component:
 * - getRows: Splits metrics into rows of 4 for the main performance metrics box.
 * - isAvailable: Checks if a metrics array is valid and non-empty.
 * - getModalRows: Splits metrics into rows of 4 for the modal dialog.
 */

// Helper to get metrics in rows of 4 for main box
export const getRows = (metricsArr, expanded) => {
  if (!expanded) {
    return [metricsArr.slice(0, 4)];
  }
  return [metricsArr.slice(0, 4), metricsArr.slice(4, 8), metricsArr.slice(8, 12)];
};

// Helper to check if an array is available (not null, not empty, and is an array)
export const isAvailable = (arr) => {
  return arr && Array.isArray(arr) && arr.length > 0;
};

// Helper to get metrics in rows of 4
export const getModalRows = (metricsArr) => [metricsArr.slice(0, 4), metricsArr.slice(4, 8), metricsArr.slice(8, 12)];

/**
 * Returns the sheet name for a given workbook name.
 * If workBookName is 'Total Claims Trend', returns 'Total Trend'.
 * Otherwise returns null.
 * @param {string} workBookName
 * @returns {string|null}
 */
export const getSheetNameByWorkBookName = (workBookName) => {
  if (workBookName === 'Total Claims Trend') {
    return 'Total Trend';
  } else if (workBookName === 'Med Claims Trend') {
    return 'Medical Trend';
  } else if (workBookName === 'Rx Claims Trend') {
    return 'Rx Trend';
  } else if (workBookName === 'Surplus/Deficit') {
    return 'Surplus Deficit';
  } else if (workBookName === 'Current Active Members') {
    return 'Current Active Members';
  } else if (workBookName === 'Gross Cost by Month') {
    return 'Gross Cost by Month';
  } else if (workBookName === 'Metrics') {
    return 'Metrics - HCC';
  } else if (workBookName === 'Member Cost Share Trend') {
    return 'Member Cost Share';
  } else {
    return 'Invalid WorkBook Name';
  }
};

/**
 * Converts the first letter of each space-separated name to uppercase and the rest to lowercase.
 * - Trims the input and collapses multiple spaces to a single space.
 * - Non-string inputs return an empty string.
 * Examples:
 *  "hELLO world" -> "Hello World"
 *  " multiple   WORDS here " -> "Multiple Words Here"
 * @param {string} input
 * @returns {string}
 */
export const capitalizeWords = (input) => {
  if (input == null) return '';
  const s = String(input).trim();
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
};

// Performance Metrics section hidden/shown preference, persisted per user per tenant.
const METRICS_HIDDEN_STORAGE_KEY = 'performanceMetricsHidden';

const getMetricsHiddenPrefKey = (userId, tenantCd) =>
  (userId && tenantCd ? `${userId}_${tenantCd}` : null);

// Only a plain object is a valid store; anything else (e.g. [] or "abc") is treated as empty
// so the next save replaces it instead of silently failing.
const readMetricsHiddenPrefs = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(METRICS_HIDDEN_STORAGE_KEY));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

export const getMetricsHiddenPref = (userId, tenantCd) => {
  const prefKey = getMetricsHiddenPrefKey(userId, tenantCd);
  if (!prefKey) return false;
  return readMetricsHiddenPrefs()[prefKey] === true;
};

export const setMetricsHiddenPref = (userId, tenantCd, hidden) => {
  const prefKey = getMetricsHiddenPrefKey(userId, tenantCd);
  if (!prefKey) return;
  try {
    const saved = readMetricsHiddenPrefs();
    saved[prefKey] = hidden;
    localStorage.setItem(METRICS_HIDDEN_STORAGE_KEY, JSON.stringify(saved));
  } catch (_) { /* storage unavailable — state still applies for this render */ }
};
